<template>
  <v-row>
    <v-dialog v-model="showDialog" scrollable max-width="800px">
      <v-card>
        <v-card-title>
          <v-panel>
            <span style="font-size: 20px;">
              {{
                !formData.id
                  ? $t("wkt.admin.training-enrollment.new-window.title")
                  : $t("wkt.admin.training-enrollment.edit-window.title")
              }}
            </span>
          </v-panel>
        </v-card-title>
        <v-card-text @wheel="onWheel">
          <v-container>
            <v-form ref="form">
              <v-row>
                <v-col cols="12">
                  <v-autocomplete
                    v-if="!formData.id"
                    v-model="formData.employeeId"
                    :items="employees"
                    :item-text="getEmployeeText"
                    item-value="id"
                    :label="$t('wkt.admin.training-enrollment.edit-window.worker')"
                    :rules="[validationRules.required]"
                    :disabled="isReadOnly"
                    :menu-props="trainingEnrollmentDialogMenuProps"
                    clearable
                  />
                  <v-text-field
                    v-else
                    :value="employeeDisplayName"
                    :label="$t('wkt.admin.training-enrollment.edit-window.worker')"
                    disabled
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-if="!formData.id"
                    :value="$t('wkt.admin.training-enrollment.status.enrolled')"
                    :label="$t('wkt.admin.training-enrollment.edit-window.status')"
                    disabled
                  />
                  <v-select
                    v-else
                    v-model="formData.status"
                    :items="statusOptions"
                    item-text="name"
                    item-value="id"
                    :label="$t('wkt.admin.training-enrollment.edit-window.status')"
                    :disabled="isReadOnly"
                    :menu-props="trainingEnrollmentDialogMenuProps"
                    @change="onStatusChanged"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-menu ref="resultDateMenu" v-model="resultDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y max-width="290px" min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="resultDateFormatted"
                        :label="$t('wkt.admin.training-enrollment.edit-window.result-date')"
                        v-on="on"
                        v-bind="attrs"
                        readonly
                        :disabled="isReadOnly || !supportsResultData"
                        prepend-icon="mdi-calendar"
                      />
                    </template>
                    <v-date-picker v-model="resultDate" :first-day-of-week="1" :locale="$i18n.locale" no-title @input="resultDateMenu = false" />
                  </v-menu>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="formData.resultValue"
                    :label="$t('wkt.admin.training-enrollment.edit-window.result-value')"
                    :disabled="isReadOnly || !supportsResultData"
                    maxlength="500"
                    counter="500"
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="formData.observations"
                    :label="$t('wkt.admin.training-enrollment.edit-window.observations')"
                    auto-grow
                    rows="3"
                    maxlength="2000"
                    counter="2000"
                    :disabled="isReadOnly"
                  />
                </v-col>
              </v-row>

              <v-row v-if="!formData.id">
                <v-col cols="12">
                  <span style="color: #757575; font-size: 13px;">
                    {{ $t("wkt.admin.training-enrollment.edit-window.create-note") }}
                  </span>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="#185EA9"
            text
            @click="onSave()"
            style="font-size: 0.9em; font-weight: 400;"
            :disabled="isReadOnly"
          >
            {{ $t("wkt.admin.common.dialog-buttons.ok").toUpperCase() }}
          </v-btn>
          <v-btn
            text
            @click="showDialog = false"
            style="font-size: 0.9em; font-weight: 400;"
          >
            {{ $t("wkt.admin.common.dialog-buttons.cancel").toUpperCase() }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import BaseModule from "../../common/BaseModule.js";
import { Constants } from "../../common/Constants.js";

export default {
  name: "TrainingEnrollmentDialog",
  mixins: [BaseModule],
  data() {
    return {
      showDialog: false,
      isReadOnly: false,
      employees: [],
      trainingEnrollmentDialogMenuProps: {
        zIndex: 260,
        contentClass: "training-enrollment-dialog-menu",
      },
      formData: {},
      resultDate: null,
      resultDateMenu: false,
      resultDateFormatted: null,
      trainingAction: null,
    };
  },
  computed: {
    statusOptions() {
      return [
        { id: 0, name: this.$t("wkt.admin.training-enrollment.status.pending-approval") },
        { id: 1, name: this.$t("wkt.admin.training-enrollment.status.pending-selection") },
        { id: 2, name: this.$t("wkt.admin.training-enrollment.status.enrolled") },
        { id: 3, name: this.$t("wkt.admin.training-enrollment.status.rejected") },
        { id: 4, name: this.$t("wkt.admin.training-enrollment.status.completed") },
        { id: 5, name: this.$t("wkt.admin.training-enrollment.status.not-pass") },
        { id: 6, name: this.$t("wkt.admin.training-enrollment.status.no-show") },
        { id: 7, name: this.$t("wkt.admin.training-enrollment.status.canceled") },
      ];
    },
    supportsResultData() {
      return [
        Constants.TRAINING_ENROLLMENT_STATUS_COMPLETED,
        Constants.TRAINING_ENROLLMENT_STATUS_NOT_PASS,
        Constants.TRAINING_ENROLLMENT_STATUS_NO_SHOW,
      ].includes(this.formData.status);
    },
    employeeDisplayName() {
      if (!this.formData.employeeFirstname && !this.formData.employeeLastname) {
        return "";
      }
      return `${this.formData.employeeLastname || ""}, ${this.formData.employeeFirstname || ""}`.replace(/^,\s*/, "");
    },
  },
  mounted() {
    this.installShadowMenuWheelPatch({
      menuSelector: ".training-enrollment-dialog-menu",
      handlerKey: "__tweemTrainingEnrollmentManualWheelHandler",
    });
  },
  beforeDestroy() {
    this.teardownShadowMenuWheelPatch();
  },
  methods: {
    onWheel(event) {
      if (!event) {
        return;
      }

      event.stopPropagation();
    },
    open(payload) {
      this.showDialog = true;
      this.isReadOnly = !!payload?.isReadOnly;
      this.employees = payload?.employees || [];
      this.trainingAction = payload?.trainingAction || null;

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      if (payload?.enrollment) {
        this.formData = {
          ...payload.enrollment,
        };
      } else {
        this.formData = {
          trainingActionId: this.trainingAction?.id || null,
          employeeId: null,
          observations: "",
          resultValue: "",
          status: Constants.TRAINING_ENROLLMENT_STATUS_ENROLLED,
        };
      }

      this.resultDate = this.simpleDateToIso(this.formData.resultDate);
      this.onStatusChanged(this.formData.status);
    },
    getEmployeeText(item) {
      if (!item) {
        return "";
      }
      const fullName = `${item.lastname || ""}, ${item.firstname || ""}`.replace(/^,\s*/, "");
      const context = [item.departmentName, item.workplaceName].filter(Boolean).join(" - ");
      return context ? `${fullName} - ${context}` : fullName;
    },
    onStatusChanged(status) {
      if (status === undefined || status === null) {
        return;
      }
      if (!this.supportsResultData) {
        this.formData.resultValue = null;
        this.resultDate = null;
      }
    },
    simpleDateToIso(value) {
      const date = this.convertSimpleDateToDate(value);
      return date ? this.formatLocalDateToISOStringDate(date).split(", ")[0] : null;
    },
    isoDateToSimpleDate(value) {
      return value ? this.convertDateToSimpleDate(new Date(`${value}`)) : null;
    },
    onSave() {
      if (!this.$refs.form.validate()) {
        return;
      }

      const payload = {
        id: this.formData.id || null,
        trainingActionId: this.trainingAction?.id || this.formData.trainingActionId,
        employeeId: this.formData.employeeId,
        status: this.formData.status,
        observations: this.formData.observations,
        resultValue: this.supportsResultData ? this.formData.resultValue : null,
        resultDate: this.supportsResultData ? this.isoDateToSimpleDate(this.resultDate) : null,
        enrollmentDate: this.formData.enrollmentDate || null,
        certificateDocumentId: this.formData.certificateDocumentId || null,
        procedureRequestId: this.formData.procedureRequestId || null,
      };

      if (!payload.id) {
        payload.status = Constants.TRAINING_ENROLLMENT_STATUS_ENROLLED;
      }

      this.$emit("save", payload);
      this.showDialog = false;
    },
  },
  watch: {
    resultDate(val) {
      this.resultDateFormatted = val ? this.formatISOStringDateToLocalDate(val) : null;
    },
  },
};
</script>
